<template>
  <div :class="$style['breakpoint-table']">
    <mds-table fixed-first-col>
      <mds-thead>
        <mds-th
          v-for="(header, index) in headers"
          :key="index"
          :right-aligned="header.align === 'right'"
          :style="header.style"
        >
          <div :class="$style['header-cell-container']">
            <span :class="$style['header-cell-text']" :title="header.text">{{ header.text }}</span>
            <mds-button
              v-if="header.fieldName === 'breakdown'"
              :id="`breakdown-fee-${index}`"
              :class="$style['dropdown-button']"
              variation="icon-only"
              icon="caret-circle-down"
              size="small"
              type="button"
              @click="header.showActionMenu = !header.showActionMenu"
              >Action</mds-button
            >
          </div>
          <mds-popover
            v-model="header.showActionMenu"
            :triggered-by="`breakdown-fee-${index}`"
            :position="['bottom-center']"
            width="200px"
            @mds-popover-dismissed="header.isRenameMode = false"
          >
            <mds-form v-if="header.isRenameMode">
              <mds-input v-model="header.name" size="small" :label="translate('feeColumnName')">
              </mds-input>
              <mds-button-container right-aligned>
                <mds-button
                  variation="primary"
                  type="button"
                  :disabled="isRenameInvalid(header.name)"
                  @click="handleRenameColumnName(header.name, header.breakdownItemIndex)"
                >
                  {{ translate('rename') }}
                </mds-button>
              </mds-button-container>
            </mds-form>
            <div v-else-if="header.showActionMenu">
              <strong>{{ translate('action') }}</strong>
              <mds-list-group :class="$style['breakpoint-table-dropdown-list']">
                <mds-list-group-item @click.native="header.isRenameMode = true">{{
                  translate('rename')
                }}</mds-list-group-item>
                <mds-list-group-item @click.native="deleteFeeColumn(header)">{{
                  translate('delete')
                }}</mds-list-group-item>
              </mds-list-group>
            </div>
          </mds-popover>
        </mds-th>
      </mds-thead>
      <mds-tbody>
        <mds-tr v-for="(row, rowIndex) in rows" :key="row.sequence">
          <mds-td
            v-for="(header, colIndex) in headers"
            :key="colIndex"
            :right-aligned="header.align === 'right'"
            :style="header.style"
          >
            <template v-if="header.fieldName === 'sequence'">
              <span :class="$style['sequence-cell']">
                <template v-if="rowIndex === rows.length - 1"
                  >{{ translate('over') }}<span :class="$style['red']">*</span></template
                >
                <template v-else>{{ `${translate('tier')} ${rowIndex + 1}` }}</template>
              </span>
            </template>
            <template v-else-if="header.fieldName === 'lowValue'">
              <HypoNumberInput
                :id="`low-value-field-${rowIndex}`"
                :readonly="rowIndex === 0"
                :right-aligned="header.align === 'right'"
                :saved-value="row[header.fieldName]"
                :placeholder="translate('enterAValue')"
                :fraction-digits="amountFractionDigits"
                :error="
                  getLowValueAscendingErrorObj(rowIndex).show ||
                    getEmptyOverRowErrorObj(rowIndex).show
                "
                @update-saved-value="handleLowValueChanged($event, rowIndex, header.fieldName)"
              />
              <mds-tooltip
                :key="tooltipUpdateKey"
                variation="error"
                :text="
                  getLowValueAscendingErrorObj(rowIndex).message ||
                    getEmptyOverRowErrorObj(rowIndex).message
                "
                icon="info-circle"
                :visible="
                  showTooltip &&
                    (getLowValueAscendingErrorObj(rowIndex).show ||
                      getEmptyOverRowErrorObj(rowIndex).show)
                "
                :position="['right-center', 'bottom-center']"
                :triggered-by="`low-value-field-${rowIndex}`"
              >
              </mds-tooltip>
            </template>
            <template v-else-if="header.fieldName === 'highValue'">
              <HypoNumberInput
                readonly
                :right-aligned="header.align === 'right'"
                :saved-value="row[header.fieldName]"
                :fraction-digits="amountFractionDigits"
              />
            </template>
            <template v-else-if="header.fieldName === 'annualAssetBasedFee'">
              <HypoNumberInput
                :readonly="showBreakdown"
                :right-aligned="header.align === 'right'"
                :saved-value="row[header.fieldName]"
                is-percent
                :error="showAnnualFeeError(rowIndex)"
                @update-saved-value="handleAnnualAssetBasedFeeChanged($event, row)"
              />
            </template>
            <template v-else-if="header.fieldName === 'breakdown'">
              <HypoNumberInput
                right-aligned
                :saved-value="dynamicCellValue(row, header.name)"
                is-percent
                @update-saved-value="handleBreakDownFeeChanged($event, header.name, row)"
              />
            </template>
          </mds-td>
        </mds-tr>
      </mds-tbody>
    </mds-table>
  </div>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import MdsForm from '@mds/form';
import MdsInput from '@mds/input';
import { MdsListGroup, MdsListGroupItem } from '@mds/list-group';
import { MdsButton, MdsButtonContainer } from '@mds/button';
import { MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd } from '@mds/data-table';
import MdsTooltip from '@mds/tooltip';
import MdsPopover from 'hypo-shared-utils/src/components/ScrollablePopover';
import HypoNumberInput from 'hypo-shared-utils/src/components/HypoNumberInput';
import debounce from 'lodash.debounce';
import {
  calculateAnnualAssetBasedFee,
  isEmptyInput,
  isValidLowValue,
  autoCalculateHighValue,
  isFallInRange,
  findPrevValidRowIndex,
  findNextValidRowIndex,
} from '../../utils';

export default {
  name: 'breakpoint-table',
  components: {
    MdsForm,
    MdsInput,
    MdsButton,
    MdsButtonContainer,
    MdsPopover,
    MdsListGroup,
    MdsListGroupItem,
    HypoNumberInput,
    MdsTable,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
    MdsTooltip,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    currencySymbol: {
      type: String,
      default: '$',
    },
    showBreakdown: {
      type: Boolean,
    },
    feeScheduleDetails: Array,
    amountFractionDigits: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      headers: [],
      rows: [],
      showTooltip: true,
      tooltipUpdateKey: 0,
      showOverRowError: false,
    };
  },
  computed: {
    staticHeaders() {
      return [
        {
          text: '',
          fieldName: 'sequence',
          style: {
            width: '80px',
          },
        },
        {
          text: `${this.translate('lowValue')} ${this.currencySymbol}`,
          fieldName: 'lowValue',
          align: 'right',
          style: {
            width: '160px',
          },
        },
        {
          text: `${this.translate('highValue')} ${this.currencySymbol}`,
          fieldName: 'highValue',
          align: 'right',
          style: {
            width: '160px',
          },
        },
        {
          text: `${this.translate('annualAssetBasedFee')}`,
          fieldName: 'annualAssetBasedFee',
          align: 'right',
          style: {
            width: '160px',
          },
        },
      ];
    },
    dynamicCellValue() {
      return (row, id) => {
        const breakdownItem = row.breakdown.find(item => item.feeName === id);
        return breakdownItem && breakdownItem.feeAmount;
      };
    },
    firstNotAscendingLowValueIndex() {
      for (let i = 0; i < this.rows.length; i += 1) {
        const currLowValue = this.rows[i].lowValue;
        if (
          !isEmptyInput(currLowValue) &&
          !isFallInRange(this.rows, i, currLowValue, this.amountFractionDigits)
        ) {
          const prevLowValue = this.rows[findPrevValidRowIndex(this.rows, i)].lowValue;
          const nextIndex = findNextValidRowIndex(this.rows, i);
          return prevLowValue === currLowValue ? i : nextIndex;
        }
      }
      return -1;
    },
    updaterDeps() {
      return [this.feeScheduleDetails, this.showBreakdown];
    },
  },
  watch: {
    updaterDeps: {
      handler() {
        this.setTableData();
      },
    },
  },
  mounted() {
    this.$el.addEventListener('scroll', this.updateTooltip);
  },
  created() {
    this.setTableData();
  },
  beforeDestroy() {
    this.$el.removeEventListener('scroll', this.updateTooltip);
  },
  methods: {
    updateTooltip: debounce(function(e) {
      const { scrollLeft } = e.target;
      this.showTooltip = scrollLeft <= 150;
      this.tooltipUpdateKey += 1;
    }, 50),
    setTableData() {
      this.rows = this.setRows();
      this.headers = this.transformHeaders();
    },
    countDecimalPlaces(value) {
      const parts = value.toString().split('.');
      if (parts.length === 1) {
        return 0;
      }
      return parts[1].length;
    },
    transformHeaders() {
      const { breakdown = [] } = this.rows[0];
      if (this.showBreakdown && breakdown.length > 0) {
        const dynamicHeaders = breakdown.map((item, index) => {
          return {
            text: `${item.feeName} %`,
            fieldName: 'breakdown',
            align: 'right',
            style: {
              width: '160px',
            },
            breakdownItemIndex: index,
            name: item.feeName,
            showActionMenu: false,
            isRenameMode: false,
          };
        });
        return this.staticHeaders.concat(dynamicHeaders);
      }
      return this.staticHeaders;
    },
    setRows() {
      let rows = autoCalculateHighValue(this.feeScheduleDetails, this.amountFractionDigits, false);
      rows = rows.map(row => {
        return {
          ...row,
          validity: row.validity || {},
        };
      });
      return rows;
    },
    handleLowValueChanged(value, rowIndex, fieldName) {
      if (rowIndex === this.rows.length - 1) {
        this.showOverRowError = true;
      }
      this.rows[rowIndex].lowValue = value;
      // Modifying current row might impact previous/next row's validity
      this.rows.forEach((item, index) => {
        item.validity[fieldName] = isValidLowValue(
          this.rows,
          index,
          item.lowValue,
          this.amountFractionDigits
        );
        if (isEmptyInput(item.lowValue) && index !== this.rows.length - 1) {
          item.validity[fieldName] = undefined;
        }
      });
      this.emitData();
    },
    handleBreakDownFeeChanged(value, columnName, row) {
      this.rows = this.rows.map(item => {
        if (item.sequence === row.sequence) {
          const breakdown = item.breakdown.map(field => {
            if (field.feeName === columnName) {
              return {
                ...field,
                feeAmount: value || 0,
              };
            }
            return field;
          });
          const annualAssetBasedFee = calculateAnnualAssetBasedFee(breakdown);
          return {
            ...item,
            annualAssetBasedFee,
            breakdown,
          };
        }
        return item;
      });
      this.emitData();
    },
    handleAnnualAssetBasedFeeChanged(value, row) {
      this.rows = this.rows.map(item => {
        if (item.sequence === row.sequence) {
          return {
            ...item,
            annualAssetBasedFee: value || 0,
          };
        }
        return item;
      });
      this.emitData();
    },
    getLowValueAscendingErrorObj(rowIndex) {
      if (rowIndex === this.firstNotAscendingLowValueIndex) {
        return { show: true, message: this.translate('lowValueErrMsg') };
      }
      return { show: false };
    },
    getEmptyOverRowErrorObj(rowIndex) {
      if (
        this.showOverRowError &&
        rowIndex === this.rows.length - 1 &&
        isEmptyInput(this.rows[rowIndex].lowValue)
      ) {
        return { show: true, message: this.translate('breakpointTableOverLowValueErrMsg') };
      }
      return { show: false };
    },
    showAnnualFeeError(rowIndex) {
      const row = this.rows[rowIndex];
      // Ignore not required rows with invalid lowValue
      if (row.lowValue === null && rowIndex !== this.rows.length - 1) {
        return false;
      }
      return row.annualAssetBasedFee > 100;
    },
    deleteFeeColumn(header) {
      this.headers = this.headers.filter(h => h.name !== header.name);
      this.rows = this.rows.map(row => {
        const breakdown = row.breakdown.filter(i => i.feeName !== header.name);
        const annualAssetBasedFee = calculateAnnualAssetBasedFee(breakdown);
        return {
          ...row,
          annualAssetBasedFee,
          breakdown,
        };
      });
      this.emitData();
    },
    addRow() {
      const rowsLen = this.rows.length;
      const nonOverRows = this.rows.slice(0, rowsLen - 1);
      const overRow = this.rows[rowsLen - 1];
      overRow.sequence += 1;
      const lastNonOver = nonOverRows[nonOverRows.length - 1];
      const sequence = (lastNonOver && lastNonOver.sequence) || 0;
      const { breakdown = [] } = this.rows[0];
      const newBreakDown = breakdown.map(i => {
        return {
          id: i.id,
          feeName: i.feeName,
          feeAmount: 0,
        };
      });
      nonOverRows.push({
        sequence: sequence + 1,
        lowValue: null,
        highValue: null,
        annualAssetBasedFee: 0,
        breakdown: newBreakDown,
      });
      this.rows = nonOverRows.concat(overRow);
      this.emitData();
    },
    addFeeColumn(columnName) {
      this.headers.push({
        text: `${columnName} %`,
        fieldName: 'breakdown',
        align: 'left',
        name: columnName,
        showActionMenu: false,
        isRenameMode: false,
      });
      this.rows = this.rows.map(row => {
        const { id } = row.breakdown[row.breakdown.length - 1] || { id: 0 };
        const breakdown = [
          ...row.breakdown,
          {
            id: id + 1,
            feeName: columnName,
            feeAmount: 0,
          },
        ];
        return {
          ...row,
          breakdown,
        };
      });
      this.emitData();
    },
    isRenameInvalid(newColumnName) {
      return (
        newColumnName === '' ||
        this.rows[0].breakdown.filter(i => i.feeName === newColumnName).length > 0
      );
    },
    handleRenameColumnName(newColumnName, colIndex) {
      this.rows = this.rows.map(row => {
        row.breakdown[colIndex].feeName = newColumnName;
        return row;
      });
      this.emitData();
    },
    emitData() {
      this.$emit('details-updated', this.rows);
    },
    validateInput() {
      this.showOverRowError = true;
      this.rows = this.rows.map((row, index, arr) => {
        if (isEmptyInput(row.lowValue) && index !== arr.length - 1) {
          return row;
        }
        const lowValueValidity = isValidLowValue(
          this.rows,
          index,
          row.lowValue,
          this.amountFractionDigits
        );
        return {
          ...row,
          validity: {
            ...row.validity,
            lowValue: lowValueValidity,
            annualAssetBasedFee: row.annualAssetBasedFee <= 100,
          },
        };
      });
      this.emitData();
    },
  },
};
</script>

<style lang="scss" module>
@import '@mds/typography';

.breakpoint-table-dropdown-list {
  margin-top: $mds-space-1-and-a-half-x;
  .mds-list-group-item__link-inner.mds-list-group-item__link-inner {
    padding-left: 0;
  }
}

.breakpoint-table {
  margin-top: $mds-space-1-and-a-half-x;
  .header-cell-container {
    display: flex;
    .dropdown-button {
      margin-left: $mds-space-three-quarter-x;
      box-shadow: none;
    }

    .header-cell-text {
      display: inline-block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .sequence-cell {
    font-size: 12px;
    font-weight: 700;
    .red {
      margin-left: $mds-space-half-x;
      color: $mds-feedback-color-error;
    }
  }
}
</style>
